import { SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
import DirectSearchInfo from './DirectSearchInfo';
import Chip from 'components/base/Chip';
import { HScrollBox } from 'components/common/HScrollBox';
import { invisible_scroll_bar } from 'components/products/details/styles';
import SearchFilterSheet from 'components/search/common/SearchFilter/SearchFilterSheet';
import useSearchChips, { SearchOptionChip, SEARCH_OPTION_TYPE } from 'components/search/hooks/useSearchChips';
import { SEARCH_FILER_REF_TYPE } from 'components/search/hooks/useSearchFilterRefs';
import { useSearchQueryString } from 'hooks';
import { bg_primary, blue600 } from 'styles';
import { SearchFilter as SearchFilterItem } from 'util/search_utils';
import { Log, useTracker } from 'util/Tracker';

interface Props {
  /**
   * 검색필터 에서 적재되는 ubl 을 정의합니다.
   */
  search_ubl: Omit<Log, 'category'>;
  /**
   * 외부에서 검색필터 시트를 제어하여 사용할 경우 해당 속성으로 필터시트를 열 수 있습니다.
   */
  open_search_filter_sheet?: boolean;
  /**
   * 카테고리 영역을 필터 조건에 없이 고정하고 싶을 경우 사용합니다. (ex. 카테고리검색)
   */
  fixed_category_id_list?: string[];
  /**
   * 필터검색조건에 검색어까지 추가적으로 필요한 경우 정의합니다. (ex. 검색페이지)
   */
  keyword?: string;
  /**
   * 검색필터 영역에 커스텀 css 를 넣어 스타일 할 경우 정의합니다.
   */
  custom_css?: SerializedStyles;
  /**
   * 정렬 필터를 표시할지 여부를 정의합니다.
   * @default true
   */
  is_show_sort_filter?: boolean;
  /**
   * 검색필터에서 초기화 버튼 클릭시 처리할 함수를 정의 합니다.
   */
  onReset: (reset_query_string: string) => void;
  /**
   * 검색필터시트가 닫히는 시점에 호출되는 이벤트로 주로 open_search_filter_sheet 를 통한 제어시점에 사용합니다.
   */
  onCloseSearchFilterSheet?: () => void;
}
/**
 * 검색 Chip
 * 검색에 대한 정렬, 세부필터 전부 해당 컴포넌트에서 담당
 * figma: https://www.figma.com/file/MOTOYx1hduCqWzghzjzSt2/%EA%B2%80%EC%83%89-%EA%B2%B0%EA%B3%BC-%ED%95%84%ED%84%B0-%EC%B9%B4%ED%85%8C%EA%B3%A0%EB%A6%AC-%EC%B6%94%EA%B0%80?node-id=920%3A64130&fuid=1009721586096356355
 * @component
 */
const SearchFilter: FC<Props> = ({
  search_ubl,
  fixed_category_id_list,
  open_search_filter_sheet = false,
  custom_css,
  is_show_sort_filter = true,
  onReset,
  onCloseSearchFilterSheet,
}) => {
  const [search_filter_type, setSearchFilterType] = useState<SEARCH_FILER_REF_TYPE | null>(
    open_search_filter_sheet ? SEARCH_FILER_REF_TYPE.CATEGORY : null,
  );
  const [open_filter_sheet, setOpenFilterSheet] = useState(false);
  const router = useRouter();
  const tracker = useTracker();
  const { search_option_chips } = useSearchChips(!fixed_category_id_list);
  const { search_props, sort_type, is_selected_sort_type, reset_query_string } = useSearchQueryString();

  useEffect(() => {
    setSearchFilterType(open_search_filter_sheet ? SEARCH_FILER_REF_TYPE.CATEGORY : null);
  }, [open_search_filter_sheet]);

  const handleClostSheetClick = () => {
    onCloseSearchFilterSheet?.();
    setSearchFilterType(null);
    setOpenFilterSheet(false);
  };

  const handleSearchClick = ({ category_list = [], color_list = [], price, is_free_shipping }: SearchFilterItem) => {
    // max, min Price 가 검색필터에 선택 안되고 쿼리스트링에는 이전 가격필터가 있는 상황에서 조건 병합시 이슈로 두개 필드 제외하고 추가
    const { max_price, min_price, is_fast_shipping, is_zigin_shipping, ...rest_query } = router.query;
    const search_info = omitBy(
      {
        category_list: JSON.stringify(category_list.map(({ name, id, depth }) => `${id}/${name}/${depth}`)),
        color_list: JSON.stringify(color_list),
        max_price: price?.max,
        min_price: price?.min,
        is_free_shipping,
        is_zigin_shipping,
        is_fast_shipping,
        sort_type: is_selected_sort_type ? sort_type : null,
      },
      isNil,
    );

    router.replace({
      pathname: router.pathname,
      query: { ...rest_query, ...search_info },
    });
  };

  const handleSearchOptionChipClick = (search_chip: SearchOptionChip) => {
    tracker.addLog({
      category: 'click',
      object_section: 'filter_chip_bar',
      object_type: 'button',
      ...search_chip.chip_ubl,
      ...search_ubl,
    });
    switch (search_chip.type) {
      case SEARCH_OPTION_TYPE.REFRESH: {
        onReset(reset_query_string);
        return;
      }
      case SEARCH_OPTION_TYPE.CATEGORY:
      case SEARCH_OPTION_TYPE.DELIVERY:
      case SEARCH_OPTION_TYPE.COLOR:
      case SEARCH_OPTION_TYPE.PRICE: {
        const filter_type = {
          [SEARCH_OPTION_TYPE.CATEGORY]: SEARCH_FILER_REF_TYPE.CATEGORY,
          [SEARCH_OPTION_TYPE.COLOR]: SEARCH_FILER_REF_TYPE.COLOR,
          [SEARCH_OPTION_TYPE.PRICE]: SEARCH_FILER_REF_TYPE.PRICE,
          [SEARCH_OPTION_TYPE.DELIVERY]: SEARCH_FILER_REF_TYPE.DELIVERY,
        }[search_chip.type];
        setSearchFilterType(filter_type);
        setOpenFilterSheet(true);
        return;
      }
    }
  };

  const init_search_filter: SearchFilterItem = omitBy(
    {
      is_fast_shipping: search_props.is_fast_shipping,
      is_free_shipping: search_props.is_free_shipping,
      is_zigin_shipping: search_props.is_zigin_shipping,
      price: search_props.price,
      color_list: search_props.color_list,
      keyword: search_props.keyword,
      sort_type: search_props.sort_type,
      category_list: fixed_category_id_list ? undefined : search_props.category_list,
    },
    isNil,
  );
  return (
    <>
      <section css={custom_css}>
        <SC.Tab>
          {search_option_chips.map((chip, index) => {
            const { name, active, classname, icon } = chip;
            return (
              <li key={`${name}-${index}`}>
                <Chip
                  text={name}
                  onClick={() => handleSearchOptionChipClick(chip)}
                  varient='outlined'
                  role='section'
                  className={classname}
                  active={active}
                  end_icon={icon}
                />
              </li>
            );
          })}
        </SC.Tab>
        <DirectSearchInfo search_ubl={search_ubl} is_show_sort_filter={is_show_sort_filter} />
      </section>
      <SearchFilterSheet
        fixed_category_id_list={fixed_category_id_list}
        init_search_filter={init_search_filter}
        search_ubl={search_ubl}
        focus_category={search_filter_type}
        onCloseSheet={handleClostSheetClick}
        onSearch={handleSearchClick}
        is_bottom_sheet_active={open_filter_sheet}
      />
    </>
  );
};

export default SearchFilter;

const SC = {
  Tab: styled(HScrollBox)`
    ${invisible_scroll_bar}
    height: 51px;
    display: flex;
    align-items: center;

    .zigin_active {
      border-color: ${blue600};
      color: ${blue600};
    }

    .reset {
      background-color: ${bg_primary};
    }
  `,
};
